import { createRouter, createWebHistory } from 'vue-router';
import BudgetCalculator from './views/BudgetCalculator.vue';
import BudgetList from './views/BudgetList.vue';
import ServicesGuide from './views/ServicesGuide.vue';
import TeamAssignmentEstimator from './views/TeamAssignmentEstimator.vue';
import TeamBudgetView from './views/TeamBudgetView.vue';
import LoginComponent from './components/auth/LoginComponent.vue';
import RegisterComponent from './components/auth/RegisterComponent.vue';
import { useAuth } from './composables/useAuth';

const routes = [
  { 
    path: '/', 
    name: 'budget-edit',
    component: BudgetCalculator,
    meta: { requiresAuth: true }
  },
  { 
    path: '/budgets', 
    name: 'budget-list',
    component: BudgetList,
    meta: { requiresAuth: true }
  },
  {
    path: '/budgets/:id/view',
    name: 'budget-view',
    component: () => import('./views/BudgetView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/services-guide',
    name: 'services-guide',
    component: ServicesGuide,
    meta: { requiresAuth: true }
  },
  {
    path: '/team-assignment',
    name: 'team-assignment',
    component: TeamAssignmentEstimator,
    meta: { requiresAuth: true }
  },
  {
    path: '/team-budgets/:id/view',
    name: 'team-budget-view',
    component: TeamBudgetView,
    meta: { requiresAuth: true }
  },
  // Authentication routes
  {
    path: '/login',
    name: 'login',
    component: LoginComponent,
    meta: { requiresGuest: true }
  },
  {
    path: '/register',
    name: 'register',
    component: RegisterComponent,
    meta: { requiresGuest: true }
  },
  // Admin routes with layout
  {
    path: '/admin',
    component: () => import('./layouts/AdminLayout.vue'),
    meta: { 
      requiresAuth: true,
      requiresAdmin: true 
    },
    children: [
      {
        path: '',
        redirect: '/admin/dashboard'
      },
      {
        path: 'dashboard',
        name: 'AdminDashboard',
        component: () => import('./views/AdminDashboard.vue')
      },
      {
        path: 'users',
        name: 'UserManagement',
        component: () => import('./views/UserManagement.vue')
      },
      {
        path: 'estimaciones',
        name: 'AdminEstimaciones',
        component: () => import('./views/EstimacionesList.vue')
      },
      {
        path: 'teams',
        name: 'AdminTeams',
        component: () => import('./views/AdminTeams.vue')
      },
      {
        path: 'groups',
        name: 'AdminGroups',
        component: () => import('./views/AdminGroups.vue')
      },
      {
        path: 'items',
        name: 'AdminItems',
        component: () => import('./views/AdminItems.vue')
      },
      {
        path: 'equipos',
        name: 'AdminEquipos',
        component: () => import('./views/AdminEquipos.vue')
      },
      {
        path: 'roles',
        name: 'AdminRoles',
        component: () => import('./views/AdminRoles.vue')
      },
      {
        path: 'peoples',
        name: 'AdminPeoples',
        component: () => import('./views/AdminPeoples.vue')
      }
    ]
  }
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

// Route guards for authentication
router.beforeEach(async (to, from, next) => {
  const { isAuthenticated, currentUser, checkAuthStatus } = useAuth();
  
  // Development debugging
  if (process.env.NODE_ENV === 'development') {
    console.log('🔍 Route Guard Debug:', {
      to: to.path,
      from: from.path,
      requiresAuth: to.meta.requiresAuth,
      requiresAdmin: to.meta.requiresAdmin,
      requiresGuest: to.meta.requiresGuest,
      isAuthenticated: isAuthenticated.value,
      currentUser: currentUser.value,
      userRole: currentUser.value?.role
    });
  }
  
  // Check authentication status
  await checkAuthStatus();
  
  // Log auth status after check
  if (process.env.NODE_ENV === 'development') {
    console.log('🔍 After checkAuthStatus:', {
      isAuthenticated: isAuthenticated.value,
      currentUser: currentUser.value
    });
  }
  
  // Check if route requires authentication
  if (to.meta.requiresAuth && !isAuthenticated.value) {
    console.log('❌ Route requires authentication but user not authenticated, redirecting to login');
    next({ 
      name: 'login', 
      query: { redirect: to.fullPath } 
    });
    return;
  }
  
  // Check if route requires admin role
  if (to.meta.requiresAdmin && (!isAuthenticated.value || currentUser.value?.role !== 'admin')) {
    console.log('❌ Route requires admin role but user is not admin, redirecting to budget-list');
    next({ name: 'budget-list' });
    return;
  }
  
  // Check if route requires guest (not authenticated)
  if (to.meta.requiresGuest && isAuthenticated.value) {
    console.log('❌ Route requires guest but user is authenticated, redirecting to budget-list');
    next({ name: 'budget-list' });
    return;
  }
  
  console.log('✅ Route guard passed, allowing navigation to:', to.path);
  next();
});

export default router;